import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Calendar, ArrowRight, Tag } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fixImageUrl = (url) => {
  if (!url) return '';
  if (url.includes('/api/admin/news/images/')) {
    const imageId = url.split('/api/admin/news/images/')[1];
    return `${BACKEND_URL}/api/admin/news/images/${imageId}`;
  }
  return url;
};

const getArticleImage = (article) => {
  if (article.images && article.images.length > 0) {
    return fixImageUrl(article.images[0]);
  }
  if (article.image_url) {
    return fixImageUrl(article.image_url);
  }
  return '';
};

const categoryColors = {
  sustainability: 'bg-green-100 text-green-700',
  corporate: 'bg-blue-100 text-blue-700',
  quality: 'bg-purple-100 text-purple-700',
  market: 'bg-orange-100 text-orange-700',
  product: 'bg-slate-100 text-slate-700',
};

const categoryTranslations = {
    sustainability: 'categorySustainability',
    corporate: 'categoryCorporate',
    quality: 'categoryQuality',
    market: 'categoryMarket',
    product: 'categoryProduct',
  };

export const News = () => {
  const { t, language } = useLanguage();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedArticle, setSelectedArticle] = useState(null);

  useEffect(() => {
    fetchNews();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchNews = async () => {
    try {
      const response = await axios.get(`${API}/news`);
      setArticles(response.data.articles);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const getTitle = (titleObj) => titleObj[language] || titleObj['en'] || '';
  const getSummary = (summaryObj) => summaryObj[language] || summaryObj['en'] || '';
  const getContent = (contentObj) => contentObj[language] || contentObj['en'] || '';

  return (
    <div className="bg-slate-50 min-h-screen" data-testid="news-page">
      {/* Hero */}
      <section className="py-20 md:py-24 bg-slate-900 text-white" data-testid="news-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold uppercase tracking-tight mb-4">
            {t('newsTitle')}
          </h1>
          <p className="text-slate-300 text-lg max-w-2xl">
            {t('newsSubtitle')}
          </p>
        </div>
      </section>

      {/* News Grid */}
      <section className="py-12 md:py-20" data-testid="news-grid">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin h-8 w-8 border-4 border-slate-900 border-t-transparent rounded-full mx-auto"></div>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {articles.map((article) => (
                <article
                  key={article.id}
                  className="bg-white border border-slate-200 overflow-hidden card-lift"
                  data-testid={`news-article-${article.id}`}
                >
                  <div className="aspect-video bg-slate-100 overflow-hidden">
                    <img
                      src={getArticleImage(article)}
                      alt={getTitle(article.title)}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <span
                        className={`text-xs font-bold uppercase tracking-wider px-2 py-1 ${
                          categoryColors[article.category] || 'bg-slate-100 text-slate-700'
                        }`}
                      >
                        {t(categoryTranslations[article.category]) || article.category}
                      </span>
                      <div className="flex items-center text-slate-500 text-sm">
                        <Calendar className="h-4 w-4 mr-1" />
                        {article.published_at}
                      </div>
                    </div>
                    <h2 className="font-heading text-xl font-bold text-slate-900 uppercase tracking-tight mb-3 line-clamp-2">
                      {getTitle(article.title)}
                    </h2>
                    <p className="text-slate-600 text-sm mb-4 line-clamp-3">
                      {getSummary(article.summary)}
                    </p>
                    <Button
                      variant="outline"
                      onClick={() => setSelectedArticle(article)}
                      className="w-full rounded-none border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white uppercase tracking-wider text-xs font-bold"
                      data-testid={`read-more-${article.id}`}
                    >
                      {t('readMore')}
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Article Modal */}
      {selectedArticle && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedArticle(null)}
          data-testid="news-modal"
        >
          <div
            className="bg-white max-w-3xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="aspect-video bg-slate-100">
              <img
                src={getArticleImage(selectedArticle)}
                alt={getTitle(selectedArticle.title)}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="p-8">
              <div className="flex items-center justify-between mb-4">
                <span
                  className={`text-xs font-bold uppercase tracking-wider px-2 py-1 ${
                    categoryColors[selectedArticle.category] || 'bg-slate-100 text-slate-700'
                  }`}
                >
                  {t(categoryTranslations[selectedArticle.category]) || selectedArticle.category}
                </span>
                <div className="flex items-center text-slate-500 text-sm">
                  <Calendar className="h-4 w-4 mr-1" />
                  {selectedArticle.published_at}
                </div>
              </div>
              <h2 className="font-heading text-3xl font-bold text-slate-900 uppercase tracking-tight mb-4">
                {getTitle(selectedArticle.title)}
              </h2>
              <p className="text-slate-600 text-lg leading-relaxed mb-6">
                {getContent(selectedArticle.content)}
              </p>
              <div className="flex items-center justify-between pt-6 border-t border-slate-200">
                <div className="text-sm text-slate-500">
                  {t('byAuthor')} {selectedArticle.author}
                </div>
                <Button
                  variant="outline"
                  onClick={() => setSelectedArticle(null)}
                  className="rounded-none"
                  data-testid="close-article"
                >
                  {t('close')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
